/**
 * Editing a shift for one or several passengers, one passenger at a time, as a client-side transaction
 * (customer request, 2026-11-02). The server has no call that replaces an order: an edit is a save of the new
 * orders and a delete of the old ones, calls that can fail apart.
 *
 * Two facts about the server shape it (FCDataBase fn_MyWayStudio_Set_Shift_Order_by_Date, DEV 2026-11-02):
 *  - a save inserts nothing into a SLOT - same relative date, same side, same time - that already holds an
 *    active order of the passenger, and still answers success;
 *  - its answer does not list the orders it created.
 * So the transaction reasons in slots and reads the passenger's orders from the server, never trusting codes
 * from the answer. For each passenger:
 *
 *   1. read the orders; the old ones are those of the edited shift;
 *   2. delete the old orders that sit in a slot the save will write (otherwise the save would skip that slot);
 *   3. save, then read again: every slot the save writes must now hold an order;
 *   4. delete the remaining old orders;
 *   5. on any failure roll back: delete what the save created and save again, from the snapshot, every old
 *      order that is gone - so the passenger is as it was;
 *   6. if the rollback fails, or the orders cannot be read, stop: the passenger is reported, nothing else is sent.
 *
 * Only the calls are injected, so the whole order of events is testable without a server.
 */

export interface SavedOrder {
    orderCode: string;
    /** The order's relative date: a delete is per date */
    date: string;
    shiftTime: string;
    side: 'pickup' | 'drop';
    /** The order's own address (D-058), to save it again in a rollback; '' when it has none */
    city?: string;
    street?: string;
    houseNum?: string;
}

export interface Slot {
    date: string;
    side: 'pickup' | 'drop';
    shiftTime: string;
}

export interface SaveResult {
    /** Every call of the save went through */
    ok: boolean;
    error?: string;
}

export type EditOutcomeStatus =
    // The new orders are saved and the old ones deleted
    | 'saved'
    // Nothing changed for this passenger: the edit failed and was rolled back
    | 'restored'
    // The rollback failed, or the orders could not be read: the passenger's orders must be checked
    | 'inconsistent'
    // Not attempted, because an earlier passenger ended inconsistent
    | 'skipped';

export interface EditOutcome {
    passId: string;
    status: EditOutcomeStatus;
    error?: string;
}

export interface EditCalls {
    /** The passenger's orders on the server, on every date the edit touches; rejects when they cannot be read */
    readOrders: (passId: string) => Promise<SavedOrder[]>;
    /** True for an order of the edited shift, which the edit replaces */
    isReplaced: (order: SavedOrder) => boolean;
    /** The slots the save writes, the same for every passenger */
    targetSlots: Slot[];
    save: (passId: string) => Promise<SaveResult>;
    /** Saves one old order again, as it was (rollback); resolves true when the server confirmed */
    recreate: (passId: string, order: SavedOrder) => Promise<boolean>;
    /** Deletes the given orders of one date; resolves true when the server confirmed */
    remove: (passId: string, orders: SavedOrder[]) => Promise<boolean>;
}

export interface EditTexts {
    saveFailed: string;
    deleteFailed: string;
    notSaved: string;
    readFailed: string;
}

export const slotKey = (o: Slot) => `${o.date}|${o.side}|${o.shiftTime}`;

/** The orders after the save that the passenger did not have before it */
export const newOrdersOf = (before: SavedOrder[], after: SavedOrder[]): SavedOrder[] => {
    const known = new Set(before.map((o) => String(o.orderCode)));
    const seen = new Set<string>();
    return after.filter((o) => {
        const code = String(o.orderCode);
        if (!code || known.has(code) || seen.has(code)) return false;
        seen.add(code);
        return true;
    });
};

const attempt = async (call: () => Promise<boolean>): Promise<boolean> => {
    try {
        return await call();
    } catch {
        return false;
    }
};

/** One delete call per date, in date order; true only if every one was confirmed */
const removeByDate = async (calls: EditCalls, passId: string, orders: SavedOrder[]): Promise<boolean> => {
    const byDate = new Map<string, SavedOrder[]>();
    orders.forEach((o) => byDate.set(o.date, [...(byDate.get(o.date) || []), o]));

    const dates = Array.from(byDate.keys()).sort();
    for (let i = 0; i < dates.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const confirmed = await attempt(() => calls.remove(passId, byDate.get(dates[i]) || []));
        if (!confirmed) return false;
    }
    return true;
};

/**
 * Back to the snapshot: deletes the orders the save created, then saves again every old order that is no longer
 * on the server. Reads to know what is there, so it also repairs a delete that failed half way.
 */
const rollback = async (
    calls: EditCalls,
    passId: string,
    before: SavedOrder[],
    old: SavedOrder[],
    error: string
): Promise<EditOutcome> => {
    let now: SavedOrder[];
    try {
        now = await calls.readOrders(passId);
    } catch {
        return { passId, status: 'inconsistent', error };
    }

    const created = newOrdersOf(before, now);
    if (created.length && !(await removeByDate(calls, passId, created))) {
        return { passId, status: 'inconsistent', error };
    }

    const stillThere = new Set(now.map((o) => String(o.orderCode)));
    const gone = old.filter((o) => !stillThere.has(String(o.orderCode)));
    for (let i = 0; i < gone.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        if (!(await attempt(() => calls.recreate(passId, gone[i])))) return { passId, status: 'inconsistent', error };
    }

    return { passId, status: 'restored', error };
};

const editOne = async (calls: EditCalls, passId: string, texts: EditTexts): Promise<EditOutcome> => {
    // -- 1. Nothing is sent for a passenger whose orders cannot be read first
    let before: SavedOrder[];
    try {
        before = await calls.readOrders(passId);
    } catch {
        return { passId, status: 'restored', error: texts.readFailed };
    }

    const old = before.filter(calls.isReplaced);
    const targets = new Set(calls.targetSlots.map(slotKey));
    const blocking = old.filter((o) => targets.has(slotKey(o)));
    const rest = old.filter((o) => !targets.has(slotKey(o)));

    // -- 2. Free the slots the save writes: the server would keep the old order there and skip the new one
    if (blocking.length && !(await removeByDate(calls, passId, blocking))) {
        return rollback(calls, passId, before, old, texts.deleteFailed);
    }

    // -- 3. Save, then check that every slot the save writes holds an order now
    let saved: SaveResult;
    try {
        saved = await calls.save(passId);
    } catch {
        saved = { ok: false, error: texts.saveFailed };
    }

    let after: SavedOrder[];
    try {
        after = await calls.readOrders(passId);
    } catch {
        // A save that went through, or half through, left orders that cannot be told apart now
        return { passId, status: 'inconsistent', error: saved.error || texts.readFailed };
    }

    const filled = new Set(after.map(slotKey));
    const covered = calls.targetSlots.every((slot) => filled.has(slotKey(slot)));
    if (!saved.ok || !covered) {
        return rollback(calls, passId, before, old, saved.error || texts.notSaved);
    }

    // -- 4. Delete the remaining old orders
    if (await removeByDate(calls, passId, rest)) return { passId, status: 'saved' };

    return rollback(calls, passId, before, old, texts.deleteFailed);
};

/**
 * Runs the edit for each passenger in order. After an 'inconsistent' passenger the rest are 'skipped': the
 * connection or the server is failing, and every further call could leave another passenger half edited.
 */
export const runEditTransaction = async (
    passIds: string[],
    calls: EditCalls,
    texts: EditTexts,
    onProgress?: (done: number, current: number) => void
): Promise<EditOutcome[]> => {
    const outcomes: EditOutcome[] = [];
    let stopped = false;

    for (let i = 0; i < passIds.length; i += 1) {
        if (stopped) {
            outcomes.push({ passId: passIds[i], status: 'skipped' });
        } else {
            onProgress?.(i, i + 1);
            // eslint-disable-next-line no-await-in-loop
            const outcome = await editOne(calls, passIds[i], texts);
            outcomes.push(outcome);
            if (outcome.status === 'inconsistent') stopped = true;
        }
    }
    onProgress?.(passIds.length, passIds.length);

    return outcomes;
};

const editTransaction = { runEditTransaction, newOrdersOf, slotKey };

export default editTransaction;
